// What the app shows: what the server last said, with the writes that have not
// gone out yet folded over it.
//
// #207's U3. `src/data/appStore.js` (U1) gives back what the server said; the
// queue holds what the teacher said; this file is where the two meet, so a
// teacher who saves offline sees the change they just made (#237).
//
// ── Derived from the live queue on every read. Never copied. ───────────────
//
// A constraint on the *shape*: `records` is a function this file calls, and it
// must be the live queue's own — `() => queue.records()`. Not a mirror kept in
// a context, not an optimistic table written at enqueue, not a snapshot taken
// when a provider mounted.
//
// A copy would leave a dead-lettered batch's change on the screen forever,
// across reloads (failure 2 of #207's plan). Deriving means the overlay cannot
// outlive the write: once the record is gone from the store, the next read
// does not see it. That covers `discarded` (removed by `discard`) and `dead`
// (still stored, and **excluded here** — see `isLive`) without this file
// knowing either word.
//
// **`acknowledged` is not in that list (#410).** A batch the server has taken
// is still folded, briefly, through a **second** function —
// `queue.confirmed()`, read on every read exactly as `records` is, and bounded
// in the queue rather than here. Nothing on screen can outlive what the queue
// holds, because this file asks the queue every time. See `stillAwaitingEcho`.
//
// ── Live is `pending` or `inflight`, named rather than negated ─────────────
//
// `queue.depth()` asks `state !== 'dead'`: it counts what is owed, and an
// unrecognised state is still owed. This asks *may I show this to a teacher as
// though it had happened*, so it names the two states affirmatively, and an
// unknown state is hidden. A change that is owed and not displayed is a screen
// one snapshot behind; a change displayed that will never land is failure 2.
//
// ── Correct when the same batch is dispatched twice (#238) ─────────────────
//
// #238's reclaim takes a batch back from a tab it judges dead, on a test that
// **cannot tell a gone tab from a frozen one**, so a batch can go out twice —
// safe by identity, with #199's ordering residual.
//
// **The overlay is a function of a record's `ops` and `fields` alone.** It
// never reads `owner`, `inflightAt`, `attempts`, `notBefore` or `reason`, and
// treats `pending` and `inflight` identically. So a reclaim cannot change what
// is on the screen, and two tabs dispatching one batch each see the one record
// in the IndexedDB database they share. `test/ui/overlay.test.jsx` pins the
// invariance.
//
// ── This read never waits on a network ────────────────────────────────────
//
// A read runs on every render, so it awaits the store and the queue's
// **storage**, and nothing else (#239). It never calls `drain`, never awaits an
// `enqueue`, never touches the barrier, and its reads are started together. A
// blocked batch, a dead one, a machine with no network — all read at the speed
// of IndexedDB. Pinned in the tests against the queue harness with its network
// switched off.
//
// ── Only the overlaid documents cross into the core ───────────────────────
//
// `reconcileDocument` costs about 35 µs a document and 2.1 µs a field
// (`docShape.js`'s header). Crossing a whole collection at its configured
// `limit()` would be roughly a second of blocked main thread across this app's
// ~30,000-document ceiling; crossing the handful of documents with unsent
// writes is under 2 ms.
//
// So a document with **no live op is returned exactly as the store handed it
// over, by reference** — not round-tripped through
// `toDocFields`/`fromDocFields`. The store has already revived its instants on
// the way out (#265). That identity is asserted in the tests.
//
// ── A pending union or removal is composed here, and only for the eye (#310) ─
//
// `reconcileDocument` **refuses** to resolve an `arrayUnion` or an
// `arrayRemove`: it keeps the remote array and tags the output row with the
// local row's transform (`core/src/reconcile.rs`'s header). Materialising the
// union *in the write* would replace a commutative server-side operation with
// a racing whole-array write (#198), and letting the local row win outright is
// #261. The core's header assigns the display union to this layer:
//
//   > A caller that reads the tag can compose the display union itself — it
//   > holds the queue rows it built `local` from, and it is the layer that may
//   > parse JSON.
//
// Without it the read-store arm shows a tag tick doing nothing (#310), while
// the snapshot arm — Firestore's cache applies an array transform immediately
// — shows it, so the two arms would disagree about a write the teacher has
// just made.
//
// **The composition is display only and it is the last thing to touch a
// document.** `writes.js` still dispatches the sentinel, Firestore still
// computes the membership, and a second device's concurrent tick is still
// kept.
//
// MMS has no offline editing (`SyncChip.jsx`'s header), so it has no behaviour
// to mirror here; the rule is that our two arms agree with each other.
//
// **Three limits:**
//
//   1. **A removal with no base row is left absent.** A union with no base row
//      composes to the elements being added — a known base has none of them,
//      and an unknown base is already reported `partial`. A *removal* with no
//      base row would have to compose to `[]`, a claim the membership is empty,
//      which nothing has said.
//   2. **Elements are matched by their JSON text.** Firestore compares set
//      elements structurally, so two objects with the same keys in a different
//      order are one element there and two here. Both set fields in this app
//      hold strings — `tags.studentIds` and `eventSeries.exceptions` — so no
//      call site can reach the difference.
//   3. **A base that is not an array is left exactly as the fold produced it**,
//      tag and all.
//
// ── A nested map is composed here too, for the same reason (#344) ─────────
//
// A patch of one leaf of a settings group (`invoicing`, `messaging`, …) is
// sent as the bare sub-patch, because the whole composed group map would lose
// one of two devices' edits to two different leaves (#344). **Firestore's
// `{ merge: true }` merges a nested map per leaf, recursively** —
// `test/integration/writeQueueFacade.test.jsx`, one level down and two.
//
// The core's fold does not: `reconcile` pairs remote and local by
// **top-level** field over opaque values, so a local `invoicing` row would
// replace the remote one whole and the leaves the teacher did not touch would
// read as defaults until a snapshot came back. `composePendingMaps` composes
// the map, here, because this is the layer that may parse JSON.
//
// **Two differences from the set composition:**
//
//   * it runs **before** the fold rather than after, because the base map it
//     composes onto is the one the fold is about to discard. The set
//     composition runs after because the core leaves the remote array standing
//     for it.
//   * it applies to a **merging** op only. A `set` is a whole-document write
//     with no merge at the server, so composing there would invent a merge
//     Firestore will not perform.
//
// The write is untouched, as it is for sets.
//
// ── What this file does not decide ────────────────────────────────────────
//
// **Not what a value means.** Every rule that resolves one write against
// another is the core's: `reconcileDocument` performs the fold and resolves a
// `deleteField` by omission. This file chooses no winner and invents no
// default; which of the core's two folds a queued op gets follows from the
// op's kind (see below), which is not choosing a winner. #207 Q3: a decision
// that changes what a value *means* belongs in the core, which Android shares.
//
// **It does parse stored values**, in one place: the pending-set and
// pending-map compositions above, which are the work the core declined and
// named this layer for. Nothing here decides which of two writers wins.
//
// **Not whether a `stale` read is allowed on screen.** #207 Q5 is *mark,
// never block*: this file refuses nothing for staleness, and passes `state`
// through untouched.
//
// **Not what a pending row looks like.** `pendingWrite` is set here; how it is
// drawn is the screens' (`PendingDot.jsx`).
//
// ── `state` is about the stored window, not about the overlay ─────────────
//
// Kept apart (#267): a studio with nothing stored reads `loading`, and if the
// teacher has queued a write in that session the overlay still returns the
// document. A caller that renders a spinner whenever `state === 'loading'`
// would hide a creation the teacher can see no other evidence of.
//
// `collectionIsLoading` and `documentIsLoading`, below, are the predicate a
// caller reaching for a spinner should use in place of
// `state === READ_STATE.loading` alone. Each is derived from `state` and what
// the queue already put on screen (`docs.length`, or `exists`), so it cannot
// drift from what `readCollection`/`readDocument` returned. `state` itself is
// not narrowed: it stays a statement about the server, which is what U1's
// receipt design keeps it.
//
// ── `partial`: not all of it is here (#268) ───────────────────────────────
//
// *Not set*, *set to nothing* and *not yet known* are kept apart by absence,
// by `null`, and by `state`/`exists`. **Not all of it is here** is a fourth
// thing.
//
// The case: a merge is folded onto a document this device has never cached, so
// `fields` starts at `[]` and the fold produces **only the fields the merge
// wrote**. Every other field the server holds is absent, and absence is what a
// field that does not exist looks like. An edit form rendering three fields of
// a twelve-field student is the failure (#267).
//
// **`partial` is derived from what is known, never from what the result looks
// like** — not from the field count. The two inputs are:
//
//   1. **whether the base was known when the fold started.** A stored row is
//      known. For a single document, so is `state !== 'loading'` with
//      `exists: false`: the store distinguishes *never heard* from *the server
//      says it is not there*, and the second is a complete answer about the
//      fields — there are none. Nothing else is known. In particular, a
//      document **missing from a stored collection is not known-absent**: every
//      listener carries a mandatory `limit()` (#64), so the window is a window
//      and a document outside it exists unseen.
//   2. **what each op does to that knowledge.** A `delete` makes the base known
//      and empty. A `set` is a whole-document write — `writes.js` dispatches it
//      as `batch.set(ref, data)` with no merge — so after it the document is
//      exactly what was written, known. A `merge` adds fields and learns
//      nothing, so a merge onto an unknown base is the partial case and a merge
//      onto a known one is not: a delete followed by a merge is whole, and a
//      bare merge is not.
//
// `partial` is a claim about the *document*, where `pendingWrite` is a claim
// about the *row* and `state` about the *server window*. What a screen does
// about it is the consumer's (U6): a list may render a partial row, an edit
// form must not save one back.
//
// ── The fold follows the **op**, not the conflict class ──────────────────
//
// Folding by `conflictClassFor(path)` alone is wrong in both directions. A
// `set` over a stored row would fold as a merge, showing fields the dispatched
// `set` is about to delete (#271). And `attendance` is `derivedCell` — *last
// write wins, whole* — so a queued **merge** over a stored attendance record
// would fold to the merge's own fields alone, while `setAttendance`'s
// `batch.set(ref, data, { merge: true })` keeps the rest at the server:
// re-marking a lesson present with no notes key would blank the lesson notes
// until the next snapshot.
//
// The conflict class is for two independent writers converging on one
// document; what the overlay folds is *our own unsent write* over *what the
// server last said*, whose effect is decided by its kind, because that is what
// `writes.js` hands to Firestore:
//
//     KIND.merge  -> batch.set(ref, data, { merge: true })   fields not named survive
//     KIND.set    -> batch.set(ref, data)                    the document is replaced
//
// `reconcile`'s own doc comment calls the first fold "what `{ merge: true }`
// means, written down as a function".
//
// The class still decides, through `requiredWriteKind` and `ruleViolations`,
// which kinds a collection may be written with at all — a `set` on a
// per-field-merge collection is a `wholeDocumentSet` defect, reported where
// defects are reported.
//
// ── Where a refusal goes ──────────────────────────────────────────────────
//
// `docShape.js` refuses a document it cannot carry — a field holding a `NaN`,
// an `undefined`, a transform newer than it knows — and names the document and
// the dotted sub-path when it does. A read that threw on one such document
// would take a whole roster down with it.
//
// So a refusal **carrying `docShape`'s own code** is caught per document, that
// document is returned **as stored and unoverlaid**, and the refusal comes back
// in `refusals` with its path. Anything else rethrows: a catch written for a
// specific failure must not become a catch for every programming error
// underneath it. A bare-string throw out of the wasm boundary (#260) has no
// `code` and is rethrown, as is a `TypeError` from this file.
//
// An unoverlaid document is **not** marked `pendingWrite`, because it is not
// showing the pending write.

import { READ_STATE } from './appStore';
import {
  UNCROSSABLE, fromDocFields, queueFieldsToDocFields, toDocFields,
} from './docShape';

/**
 * The two queue states a change may be shown for, and the two write kinds this
 * file has to tell apart — the one that removes a document and the one that
 * replaces it whole.
 *
 * Duplicated from `queue.js` and `opCodec.js` rather than imported, for the
 * reason `docShape.js` gives: `opCodec.js` imports `firebase/firestore` for its
 * sentinels, and the `ui` jest project cannot reach Firebase. `queue.js`'s own
 * state names are module-private there.
 *
 * `test/ui/overlay.test.jsx` pairs these against `opCodec.KIND` and against
 * records the real queue produced.
 */
const PENDING = 'pending';
const INFLIGHT = 'inflight';
const KIND_DELETE = 'delete';
const KIND_SET = 'set';

/**
 * The two transforms the core refuses to resolve, which this file composes for
 * the screen (#310).
 *
 * Duplicated for the reason above: the `ui` project cannot import
 * `opCodec.js`, and `docShape.js` keeps its own copies private.
 * `test/ui/overlay.test.jsx` asserts both names are members of `docShape`'s
 * exported `DOC_FIELD_TRANSFORMS`.
 */
const TRANSFORM_ARRAY_UNION = 'arrayUnion';
const TRANSFORM_ARRAY_REMOVE = 'arrayRemove';
/** A row the fold produced as an ordinary value, which is what a composed set is. */
const TRANSFORM_NONE = '';

/**
 * The two folds `reconcileDocument` performs, by the core's name for each.
 *
 * Duplicated for the same reason the kinds above are.
 * `test/ui/overlay.test.jsx` asserts what each name *does* — that one keeps a
 * remote field the write does not name and the other does not — rather than
 * comparing it to a collection's class.
 *
 * `FOLD_AS_MERGE` is `reconcile`'s own answer to "what does `{ merge: true }`
 * mean"; `FOLD_AS_REPLACE` is the local-whole one it gives `derivedCell` and
 * `appendOnly`. Both resolve `deleteField` and `arrayUnion` identically, so
 * nothing about a transform turns on this choice.
 */
const FOLD_AS_MERGE = 'perFieldMerge';
const FOLD_AS_REPLACE = 'derivedCell';

/** Re-exported so a caller needs one import, not two. */
export { READ_STATE };

/**
 * #267: the predicate a caller reaching for a spinner should use in place of
 * `state === READ_STATE.loading` alone, for a `readCollection` result.
 *
 * `state === 'loading'` is true whenever nothing has ever been stored for this
 * scope — and stays true even after the queue has put a locally-created
 * document on screen, because `state` describes the server window and
 * `docs` describes what is actually there to show. A caller that blocks on
 * `state` alone hides that creation; this is true only when there is also
 * nothing in `docs` to show instead.
 *
 * Whether a document built only from queued ops is complete enough for a
 * screen to render is `partial`'s question, not this one's (see the header).
 */
export function collectionIsLoading(read) {
  return read.state === READ_STATE.loading && read.docs.length === 0;
}

/**
 * #267's predicate for a `readDocument` result — the same rule as
 * `collectionIsLoading`, in the shape a single document read has. `exists` is
 * the field to read, per `readDocument`'s own doc comment: `state` stays
 * `loading` regardless of the fold, so a queued creation with nothing stored
 * makes `exists` true and this false. A queued delete of a document this
 * device never had a row for folds to `exists: false`, so this stays true:
 * there is nothing to show.
 */
export function documentIsLoading(read) {
  return read.state === READ_STATE.loading && !read.exists;
}

/**
 * May a queue change under `prefix` be ignored? (#389)
 *
 * `true` means *ask again*. `prefix` is `users/{uid}/{collection}/` for a
 * collection read, or the document's own full path for a document read.
 *
 * ── Why this is sound ──────────────────────────────────────────────────────
 *
 * `readCollection` and `readDocument` below fold **only** the live ops whose
 * path sits under that prefix — `liveOpsByPath` is keyed by path and neither
 * read consults the queue for anything else. So a transition that touched no
 * path under the prefix cannot have moved that read's answer.
 *
 * So the predicate lives here rather than in `writes.js`, which owns the
 * signal: the claim that a listener may skip is **this** file's, and stops
 * being true if a read here starts reading the queue globally. The two read
 * hooks also must not import `writes.js`, which reaches Firebase, and one
 * interpretation in one place keeps the collection and document hooks
 * agreeing.
 *
 * ── The direction of the error ────────────────────────────────────────────
 *
 * `true` for an unknown change, always. A `null` change is a transition
 * `queue.js` could not enumerate, and reading it as "nothing moved" would keep
 * a teacher's own write off their screen (#237). A spurious `true` costs one
 * store round trip and one fold whose answer `stableRead` discards by
 * identity.
 *
 * ── The confirmed hold (#410) ─────────────────────────────────────────────
 *
 * A held batch is released by a **receipt**, not by a queue transition. That
 * does not reach this predicate: the release is a snapshot being stored, and
 * `useStoredCollection` re-reads on every snapshot. The acknowledgement that
 * starts the hold is a transition and names its paths.
 *
 * The hold **expiring** — `confirmedHoldMs` with no listener on the scope —
 * has no signal, and needs none: a stale hold shows the value the server took,
 * corrected by the next read for any reason.
 */
export function changeTouches(change, prefix) {
  if (!change || !change.paths) return true;
  for (const path of change.paths) if (path.startsWith(prefix)) return true;
  return false;
}

// ── A read that did not change hands back the object it handed back before ──
//
// #379. A queue change wakes every mounted provider, and most re-read a
// collection the write never touched. A *fresh* but equal object from each of
// those reads makes `setRead` schedule an update React cannot bail out of, and
// every consumer's `useMemo` over `docs` recomputes behind it. Returning the
// previous object when the new one is equal lets `dispatchSetState`'s
// `Object.is` find the state unchanged, so it never reaches
// `scheduleUpdateOnFiber`; the `setError(null)` that follows takes the same
// bailout.
//
// **This is an identity fix, not a caching one.** The read is still performed
// and the queue still folded; only the answer's *identity* is reused, and only
// when the answer is equal, so it cannot serve a stale value.
//
// Sparing the read itself is the other half: `onQueueChange` carries which
// paths moved (#389, `src/data/writes.js`), `changeTouches` above says why a
// listener may skip on it, and `useStoredCollection` and `useStoredDocument`
// ask it. An unknown-extent transition, a snapshot and a mount still re-read.
const IDENTITY_CACHE_LIMIT = 256;

/**
 * Structural equality for a read result.
 *
 * Not `JSON.stringify`: an instant comes back from this file as a Firestore
 * `Timestamp` (see `reviveTimestamp`), and the SDK's value types all answer
 * `isEqual`. Two `Timestamp`s for one instant are different objects and must
 * compare equal.
 *
 * A false *unequal* costs one wasted render; a false *equal* would show a
 * stale value, so every branch below falls back to "different" rather than
 * guess.
 */
function sameValue(a, b) {
  if (Object.is(a, b)) return true;
  if (a === null || b === null) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  // `Timestamp`, `GeoPoint`, `DocumentReference`, `Bytes` — every Firestore
  // value type. Asked before the plain-object branch because their fields are
  // private and comparing those would be comparing an implementation.
  if (typeof a.isEqual === 'function' && typeof b.isEqual === 'function') {
    try {
      return a.isEqual(b) === true;
    } catch {
      return false;
    }
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, i) => sameValue(item, b[i]));
  }
  // A class this file does not know about is not compared field by field: two
  // instances with equal fields are not necessarily interchangeable, and
  // `Object.is` above has already answered the only case that is certain.
  if (Object.getPrototypeOf(a) !== Object.getPrototypeOf(b)) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => Object.prototype.hasOwnProperty.call(b, k) && sameValue(a[k], b[k]));
}

/**
 * Keep one previous answer per scope, and hand it back when the new answer
 * equals it.
 *
 * Bounded: an eviction costs one wasted render, never a wrong value. A tab
 * holds one uid and a few dozen scopes; a fresh uid per test (the
 * `integration` project) is what makes it grow.
 */
function createIdentityCache() {
  const held = new Map();
  return (key, next) => {
    const previous = held.get(key);
    if (previous !== undefined && sameValue(previous, next)) return previous;
    if (held.size >= IDENTITY_CACHE_LIMIT) held.clear();
    held.set(key, next);
    return next;
  };
}

/**
 * May this record's change be shown as though it had happened?
 *
 * Affirmative rather than `!== 'dead'`. See the header.
 */
const isLive = (record) => record.state === PENDING || record.state === INFLIGHT;

/**
 * Is a batch the server has already taken still worth folding? (#410)
 *
 * ── The gap this closes ───────────────────────────────────────────────────
 *
 * With lite dispatch on (`src/data/liteWriteFlag.js`, #410) a write goes over
 * `firebase/firestore/lite`, which enters no mutation queue. So the full SDK's
 * listener — the only reader — has no local mutation for it and learns of the
 * write only when the **watch stream** echoes it back, one round trip after
 * `commit()` resolved. Without this hold, between the queue removing the
 * record at `acknowledged` and that echo, the row would revert to its old
 * value and then change back (`npm run test:lite` covers it).
 *
 * The full SDK closes the same gap with `hasCommittedMutations`, keeping a
 * committed mutation applied to its cache until the echo lands.
 * `queue.confirmed()` is the same fact for our queue, and this is when to stop
 * believing it.
 *
 * ── `seenAt` is the echo, and it is the store's own answer ────────────────
 *
 * `appStore` stamps a receipt every time a snapshot is stored
 * (`replaceCollection`/`replaceDocument`), so `seenAt` is *when this scope was
 * last delivered*. A delivery **after** the acknowledgement is the listener
 * having spoken to the server since the server took the write, which is the
 * echo. So the rule is one comparison, with no timer and nothing copied.
 *
 * `null` — nothing ever stored for this scope — keeps the fold, because a
 * receipt that does not exist cannot be newer than anything.
 *
 * ── Two limits ────────────────────────────────────────────────────────────
 *
 *   * **Equal stamps keep folding.** `>=` rather than `>`: a snapshot stored
 *     in the same millisecond as the acknowledgement may or may not carry the
 *     write, and showing the confirmed value is the recoverable direction — it
 *     is what the server holds.
 *   * **`confirmedAt` and `seenAt` are two clocks.** Both are `Date.now()` in
 *     the app, and `readStore.js` builds the store and the queue that feed this
 *     from one tab. A test that injects a fixed clock into only one of them
 *     makes every confirmation look newer than every receipt, which holds the
 *     fold for `confirmedHoldMs` — again the recoverable direction.
 *     `test/ui/overlay.test.jsx` drives both sides with one clock.
 */
function stillAwaitingEcho(record, seenAt) {
  if (typeof seenAt !== 'number') return true;
  return record.confirmedAt >= seenAt;
}

/**
 * Every live op, grouped by the document it targets, in the order they will be
 * applied at the server.
 *
 * Across batches that is `seq`, which is the queue's own monotonic enqueue
 * order and the same order `core/src/queue.rs` dispatches per-document FIFO in.
 * Within a batch it is the order of `record.ops`, untouched — `queueDefects`
 * already refuses a batch that contradicts itself, so this does not have to
 * reconcile a batch against itself.
 */
function liveOpsByPath(records, confirmedRecords = [], seenAt = null) {
  const byPath = new Map();
  [
    ...(records || []).filter(isLive).map((record) => ({ record, pending: true })),
    ...(confirmedRecords || [])
      .filter((record) => stillAwaitingEcho(record, seenAt))
      .map((record) => ({ record, pending: false })),
  ]
    .sort((a, b) => a.record.seq - b.record.seq)
    .forEach(({ record, pending }) => {
      (record.ops || []).forEach((op) => {
        const list = byPath.get(op.path) || [];
        list.push({
          path: op.path,
          kind: op.kind,
          // Is this op still owed, or has the server already taken it? Carried
          // per op rather than per document because one document can have both
          // — a confirmed rename and a pending one — and `pendingWrite` is the
          // question *is anything here unsent*, which is an `any` over these.
          pending,
          // The fields this op owns. `record.fields` is flat across the batch
          // and carries `opId`, which is how a five-document attendance batch
          // keeps its five documents' fields apart.
          fields: (record.fields || []).filter((f) => f.opId === op.id),
        });
        byPath.set(op.path, list);
      });
    });
  return byPath;
}

/** A refusal `docShape.js` raised, as opposed to anything else that threw. */
const isCarriageRefusal = (err) => err && err.code === UNCROSSABLE;

/**
 * The JSON this row carries, or `undefined` if it is not the shape asked for.
 *
 * Never throws. A stored value came through `docShape` and parses, and a queued
 * element list was written by `opCodec.encodeFields` with `JSON.stringify`, so
 * a failure here is a row this app does not produce; declining to compose is a
 * stale screen rather than a broken read.
 */
function parseArray(text) {
  if (typeof text !== 'string' || text === '') return undefined;
  try {
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/** A JSON map: not an array, not `null`, not a scalar. */
const isMap = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * The map this row carries, or `undefined` if it is not one. `parseArray`'s
 * sibling, with its reasons: never throws, and declining is a stale screen
 * rather than a broken read.
 */
function parseMap(text) {
  if (typeof text !== 'string' || text === '') return undefined;
  try {
    const parsed = JSON.parse(text);
    return isMap(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Firestore's own nested-map merge, on two parsed maps.
 *
 * **Recursive**, as `test/integration/writeQueueFacade.test.jsx` asserts of
 * Firestore. A leaf the patch does not name survives at every depth; a leaf it
 * names is replaced whole, including when the replacement is an array or a
 * scalar landing on a map.
 */
function mergeMaps(base, patch) {
  const out = { ...base };
  Object.keys(patch).forEach((key) => {
    const next = patch[key];
    out[key] = isMap(next) && isMap(base[key]) ? mergeMaps(base[key], next) : next;
  });
  return out;
}

/**
 * A merging write's map-valued fields, composed onto the document's own (#344).
 *
 * Takes the fields as they stand **before** this op is folded and that op's
 * local rows, and returns those rows with each map value replaced by the map
 * Firestore will hold once the write lands. The fold then replaces the field
 * with an already-merged map, which is the same answer the server reaches.
 *
 * `core/src/reconcile.rs` folds by top-level field over values it keeps
 * **opaque** (it cannot see a document), so deep-merging, which means parsing
 * one, is this layer's — #310's shape, one field type over, and for the screen
 * only. The write is untouched: `useSettings` sends the bare sub-patch and
 * Firestore performs the merge.
 *
 * **Only on a merging fold.** A `set` is a whole-document write with no merge
 * at Firestore, so the caller does not call this for one; composing a `set`'s
 * rows would invent a merge the server will not perform (#261's direction).
 *
 * **Three limits:**
 *
 *   1. **Only a `TRANSFORM_NONE` row on both sides.** A sentinel is not a
 *      value, and a base row still tagged `arrayUnion` is an array the core
 *      left unresolved. No transform in the vocabulary carries a map —
 *      `deleteField` carries no value, the set transforms carry arrays,
 *      `serverTimestamp` arrives resolved to `null`, `increment` is a number —
 *      so `parseMap` alone declines each; the checks keep a transform added
 *      to the vocabulary from being merged.
 *   2. **A field with no base row is left exactly as it is.** The write is
 *      creating the map, so the patch *is* the map, which is also what the
 *      server will hold; a `partial` document (#268) is already reported as
 *      one.
 *   3. **A base that is not a map is left alone.** A scalar becoming a map is
 *      a replacement at the server too.
 */
function composePendingMaps(path, fields, opFields) {
  const rows = opFields || [];
  let out = rows;
  let changed = false;
  rows.forEach((row, at) => {
    if (row.transform !== TRANSFORM_NONE) return;
    const patch = parseMap(row.value);
    if (patch === undefined) return;
    const baseAt = (fields || []).findIndex((f) => f.field === row.field);
    if (baseAt === -1) return;
    if (fields[baseAt].transform !== TRANSFORM_NONE) return;
    const base = parseMap(fields[baseAt].value);
    if (base === undefined) return;
    if (!changed) { out = out.slice(); changed = true; }
    out[at] = { ...row, path, value: JSON.stringify(mergeMaps(base, patch)) };
  });
  return out;
}

/**
 * How two set elements are told apart. See limit 2 in the header: JSON text,
 * not Firestore's structural equality, and both set fields in this app hold
 * strings.
 */
const elementKey = (element) => JSON.stringify(element);

/**
 * The pending union or removal, applied to the array the fold left behind.
 *
 * Takes the folded `DocField[]` and **one** queued op's fields, and returns the
 * list with each set field replaced by what the teacher should see. The result
 * carries the ordinary transform: a composed row is a value, and leaving the
 * tag on it would ask every later reader to decide again what it meant.
 *
 * Called once per op rather than once at the end: the fold's output row
 * carries only the **last** transform applied to that field, so a union
 * followed by a removal leaves one row tagged `arrayRemove` and no trace of the
 * union. Composing per op replays them in the order the server will, so
 * "tick, untick, tick" ends up ticked.
 */
function composePendingSets(path, fields, opFields) {
  const sets = (opFields || []).filter(
    (f) => f.transform === TRANSFORM_ARRAY_UNION || f.transform === TRANSFORM_ARRAY_REMOVE,
  );
  if (!sets.length) return fields;

  let out = fields;
  let changed = false;
  for (const set of sets) {
    const elements = parseArray(set.value);
    if (elements === undefined) continue;
    const at = out.findIndex((f) => f.field === set.field);
    // Absent for a removal is left absent — limit 1 in the header.
    if (at === -1 && set.transform === TRANSFORM_ARRAY_REMOVE) continue;
    const base = at === -1 ? [] : parseArray(out[at].value);
    // A field whose value is not an array — limit 3.
    if (base === undefined) continue;

    let next;
    if (set.transform === TRANSFORM_ARRAY_UNION) {
      // Firestore appends the elements not already present, in the order they
      // were given, and leaves the existing order alone. `seen` grows as it
      // goes, so a union naming one element twice adds it once.
      const seen = new Set(base.map(elementKey));
      const added = [];
      elements.forEach((el) => {
        const key = elementKey(el);
        if (seen.has(key)) return;
        seen.add(key);
        added.push(el);
      });
      next = base.concat(added);
    } else {
      const removing = new Set(elements.map(elementKey));
      next = base.filter((el) => !removing.has(elementKey(el)));
    }

    const row = {
      path, field: set.field, value: JSON.stringify(next), transform: TRANSFORM_NONE,
    };
    if (!changed) { out = out.slice(); changed = true; }
    if (at === -1) out.push(row);
    else out[at] = row;
  }
  // `reconcile` guarantees field-name order (#255) and a row this function adds
  // would otherwise land at the end, so the result is sorted rather than
  // spliced into place.
  //
  // **It is the same rule the core states, for every field name this app can
  // have, and not the identical comparison** (#356): `reconcile.rs` sorts with
  // `a.field.cmp(&b.field)`, which is UTF-8 **byte** order, while `<` on
  // JavaScript strings is UTF-16 **code unit** order, as is `sortedBy` in the
  // Kotlin twin. The two disagree only where a field name carries a character
  // above the BMP. Take `U+FFFD` and `U+10000`: in UTF-8 they are `EF BF BD`
  // and `F0 90 80 80`, so the byte order the core uses puts `U+FFFD` first; in
  // UTF-16 they are `FFFD` and the surrogate pair `D800 DC00`, and `D800` is
  // the smaller code unit, so the code-unit order used here puts `U+10000`
  // first. That is, `U+FFFD` sorts after `U+10000` here and before it in the
  // core. `test/domain/fieldOrder.test.js` reads this sentence back and checks
  // it against the two comparators.
  //
  // Every Firestore field name in this app is ASCII — they come from the schema
  // and `firestore.rules`, not from anything a teacher types — and the two
  // orders are identical over ASCII. Making the three agree by construction
  // (#356) is a change to the Kotlin twin as well.
  if (!changed) return out;
  return out.sort((a, b) => {
    if (a.field === b.field) return 0;
    return a.field < b.field ? -1 : 1;
  });
}

/**
 * One document after its queued ops land on the one the store holds.
 *
 * `stored` is the plain document the snapshot delivered, or `null` for a
 * document the store does not have — a student created offline has no remote
 * row at all (#237).
 *
 * `baseKnown` says whether the caller knows the document's **whole** remote
 * field set at the point the fold starts — a stored row does, and so does a
 * store that has been told the document is absent. `stored === null` alone
 * does not: it covers both *never heard of* and *known not to be there*
 * (#268).
 *
 * Returns `{ exists, data, partial }`. `exists: false` is a **tombstone** — a
 * queued `delete` — not a document whose fields all went away (U1, #252).
 * `partial: true`: the fields here are the ones that were written, and there
 * are others this device has never seen. See the header.
 */
function foldDocument(core, path, stored, baseKnown, ops) {
  let fields = stored === null ? [] : toDocFields(path, stored);
  let exists = stored !== null;
  // Does this fold know every field the document holds? Carried through the
  // loop, because the ops change the answer in both directions, in order.
  let known = baseKnown;
  let partial = false;

  for (const op of ops) {
    if (op.kind === KIND_DELETE) {
      // A tombstone, not an empty document. A later op in the same fold may
      // bring it back — a delete followed by a set is two queued batches and
      // the second one wins, exactly as it will at the server.
      //
      // And it is a *complete* answer about the fields: after this the document
      // holds none, whatever the server had. So a merge that follows a delete
      // is whole, where the same merge on its own would not be.
      fields = [];
      exists = false;
      known = true;
      partial = false;
      continue;
    }
    if (op.kind === KIND_SET) {
      // A whole-document write — `writes.js` dispatches it as `batch.set(ref,
      // data)`, no merge — so from here the document is exactly what was
      // written, whether or not this device ever saw the old one.
      known = true;
      partial = false;
    } else if (!known) {
      // A merge onto a base nobody here has seen. The fold below starts from
      // `[]` and produces only these fields; the rest of the document is at the
      // server and is about to be absent from the result. #268.
      partial = true;
    }
    // `remote` is empty for a document that does not exist yet, so either fold
    // produces the local write whole and the offline creation appears.
    //
    // **The kind picks the fold**, because the kind is what `writes.js` hands
    // to Firestore and so decides which stored fields survive this write.
    // Whether the kind was *permitted* on this collection is `ruleViolations`'
    // question — see the header.
    //
    // The map composition (#344) runs **before** the fold: Firestore merges a
    // nested map per leaf and the core's fold replaces the whole field, so the
    // base map is read here while it is still there. On a `set` there is no
    // server-side merge to predict, so the rows go in untouched — see
    // `composePendingMaps`.
    const local = queueFieldsToDocFields(path, op.fields);
    fields = core.reconcileDocument(
      op.kind === KIND_SET ? FOLD_AS_REPLACE : FOLD_AS_MERGE,
      exists ? fields : [],
      op.kind === KIND_SET ? local : composePendingMaps(path, exists ? fields : [], local),
    );
    // #310, after the fold: the core has decided which fields survive this
    // write and declined to resolve the set ones; this composes those, for the
    // screen only. `op.fields` are the queue's own rows, so the elements are
    // the ones `opCodec` stored.
    fields = composePendingSets(path, fields, op.fields);
    exists = true;
  }

  // Nothing to be partial about when there is no document: a tombstone is a
  // complete answer, not half of one.
  return { exists, data: exists ? fields : null, partial: exists && partial };
}

/**
 * The document id at the end of a path, or null if the path is not in `prefix`.
 *
 * A Firestore document id cannot contain a `/`, and every collection in
 * `paths.js` is a direct child of `users/{uid}`, so the remainder is the id and
 * a remainder containing a slash is not this collection's to fold.
 */
function idWithin(prefix, path) {
  if (!path.startsWith(prefix)) return null;
  const rest = path.slice(prefix.length);
  return rest && !rest.includes('/') ? rest : null;
}

/**
 * A reader over one app store and one live queue.
 *
 * @param store    `createAppStore()` — what the server last said.
 * @param records  `() => Promise<record[]>` — **the live queue's own**, as in
 *                 `() => queue.records()`. See the header on why this is a
 *                 function and not a value.
 * @param confirmed `() => Promise<record[]>` — `() => queue.confirmed()`, the
 *                 batches the server has taken whose echo has not arrived
 *                 (#410). A function for the same reason `records` is, and
 *                 defaulting to none so a hand-built double needs no third
 *                 collaborator. See `stillAwaitingEcho`.
 * @param loadCore `src/lib/core.js` — the rules. Async for the same reason the
 *                 queue's is: the browser rendering fetches its own wasm.
 * @param reviveTimestamp how an encoded instant becomes whatever the provider
 *                 expects an instant to be. Passed to `fromDocFields`, and it
 *                 applies **only to overlaid documents** — a document with no
 *                 queued write is handed back as the store decoded it and is
 *                 never re-encoded.
 *
 *                 **Defaults to the store's own.** Two instants meet in one
 *                 read: the queued one this parameter revives — `docShape`
 *                 resolves `enqueuedAt` (#232) to a tagged value and
 *                 `serverTimestamp` to `null` — and the stored one `appStore`
 *                 revives on its way out of IndexedDB (#265). With two
 *                 different functions, one `readCollection` would hand back an
 *                 ISO string for rows with no pending write and a `Timestamp`
 *                 for the one just edited.
 */
export function createOverlay({
  store, records, loadCore, confirmed = async () => [], reviveTimestamp = undefined,
}) {
  // `store.reviveTimestamp` is `appStore`'s; the literal is for a hand-built
  // store double that has none, and matches `fromDocFields`' own default.
  const revive = reviveTimestamp || store.reviveTimestamp || ((iso) => iso);
  const decode = (fields) => fromDocFields(fields, { reviveTimestamp: revive });

  // Per overlay, not per module: `readStore.js` builds one of these per tab,
  // and a module-level cache would be shared by two overlays over two stores
  // — which the `ui` project builds routinely.
  const stableRead = createIdentityCache();

  /**
   * Fold one document, or hand back what is stored and say why.
   *
   * `onRefusal` is called with a refusal `docShape` raised; anything else
   * rethrows, because this catch is written for a document that cannot cross
   * and not for a bug underneath it.
   */
  function overlay(core, path, stored, baseKnown, ops, onRefusal) {
    try {
      const folded = foldDocument(core, path, stored, baseKnown, ops);
      return {
        exists: folded.exists,
        data: folded.exists ? decode(folded.data) : null,
        // **`any`, and over the ops rather than a constant** (#410). A batch
        // held for its echo is not owed — the server has it — so a row
        // showing one alone is `pendingWrite: false`, which is what the full
        // SDK reports for the same state (`hasPendingWrites` goes false at the
        // acknowledgement; `hasCommittedMutations` covers the rest).
        pendingWrite: ops.some((op) => op.pending),
        partial: folded.partial,
      };
    } catch (err) {
      if (!isCarriageRefusal(err)) throw err;
      onRefusal({ path, code: err.code, message: err.message });
      // As stored, and **not** marked pending: nothing of the queued write is
      // on this document, and saying otherwise would be a claim the fold did
      // not earn. `partial` is false for the same reason: what comes back is
      // the stored document, which is whole as the snapshot delivered it — the
      // fold that could have left fields out did not run.
      return {
        exists: stored !== null, data: stored, pendingWrite: false, partial: false,
      };
    }
  }

  return {
    /**
     * A collection as the app should show it.
     *
     * `{ docs, state, seenAt, complete, confirmed, refusals }`, where `docs` is
     * `[{ id, data, pendingWrite, partial }]`.
     *
     * `partial` is #268 and is documented in the header. `complete` beside it
     * is the *receipt's* and a different question — whether a whole window was
     * stored — so the two are not two names for one fact: a `complete`
     * collection can hold a `partial` document, and a document with no queued
     * write is never `partial` however incomplete the window is.
     *
     * `pendingWrite` sits **beside** `data` rather than inside it, so this
     * file never adds a key to a document the core might be asked about; the
     * window hooks spread it onto the row.
     *
     * Order is the receipt's — Firestore's delivered order — with documents
     * that exist **only** locally appended after it, in enqueue order. The
     * server has never ordered those, and sorting a collection here would be a
     * query engine this app does not have (#207). Most providers sort in their
     * own `useMemo`.
     */
    async readCollection(uid, collection) {
      // Started together rather than in series, and none waits on a network,
      // so the read costs one IndexedDB round trip's latency. The queue is
      // grouped below rather than in a `.then`, because which confirmed
      // batches still count depends on the receipt this same read is
      // fetching — see `stillAwaitingEcho`.
      const [stored, queued, held, core] = await Promise.all([
        store.readCollection(uid, collection),
        records(),
        confirmed(),
        loadCore(),
      ]);
      const live = liveOpsByPath(queued, held, stored.seenAt);

      const prefix = `users/${uid}/${collection}/`;
      const refusals = [];
      const push = (r) => refusals.push(r);
      const seen = new Set();
      const docs = [];

      const keep = (id, result) => {
        // A queued delete drops the row, which is what the teacher just asked
        // for and what the next snapshot will do anyway.
        if (!result.exists) return;
        docs.push({
          id, data: result.data, pendingWrite: result.pendingWrite, partial: result.partial,
        });
      };

      stored.docs.forEach(({ id, data }) => {
        seen.add(id);
        const ops = live.get(prefix + id);
        if (!ops) {
          // The fast path: no crossing, no re-encoding, the stored object by
          // reference.
          docs.push({
            id, data, pendingWrite: false, partial: false,
          });
          return;
        }
        // A stored row is a base this device knows in full.
        keep(id, overlay(core, prefix + id, data, true, ops, push));
      });

      live.forEach((ops, path) => {
        const id = idWithin(prefix, path);
        if (id === null || seen.has(id)) return;
        // **Not** a known-absent base (#268). A document missing from a stored
        // collection may be outside the listener's mandatory `limit()` window
        // (#64) — the receipt records the window that was delivered, not the
        // collection. So a merge queued against it folds onto nothing, and the
        // read says so.
        keep(id, overlay(core, path, null, false, ops, push));
      });

      // The previous answer when this one equals it — #379, see the note above
      // `sameValue`.
      return stableRead(`c:${uid}/${collection}`, {
        docs,
        state: stored.state,
        seenAt: stored.seenAt,
        complete: stored.complete,
        // Whether the server has ever answered this scope on this device
        // (#651) — the store's, passed through; `core/src/reading.rs` says
        // what it decides.
        confirmed: stored.confirmed !== false,
        refusals,
      });
    },

    /**
     * One document as the app should show it.
     *
     * `{ data, exists, state, seenAt, pendingWrite, partial, refusals }`.
     *
     * **`exists` is the field to read, not the truthiness of `data`.** Four
     * answers stay apart, three of them as they do in `appStore.js`:
     * `state === 'loading'` is *we have never been told*, `exists === false` is
     * *it is not there* — whether because the server said so or because a
     * queued delete says so — and a `data` object is the document. A queued
     * delete gives `exists: false` with `pendingWrite: true`, which is the one
     * combination that says *gone, and the server does not know yet*.
     *
     * The fourth is `partial: true` (#268) — *this is the document, and not all
     * of it is here*: `exists` is true and `data` is an object, so every check
     * above passes. A list may render it; a form that would save it back must
     * not.
     */
    async readDocument(uid, collection, id) {
      const [stored, queued, held, core] = await Promise.all([
        store.readDocument(uid, collection, id),
        records(),
        confirmed(),
        loadCore(),
      ]);
      const live = liveOpsByPath(queued, held, stored.seenAt);

      const path = `users/${uid}/${collection}/${id}`;
      const ops = live.get(path);
      if (!ops) {
        return stableRead(`d:${path}`, {
          data: stored.data,
          exists: stored.exists,
          state: stored.state,
          seenAt: stored.seenAt,
          pendingWrite: false,
          partial: false,
          refusals: [],
        });
      }
      const refusals = [];
      // A single document read knows its base in two of the store's three
      // states, and that is exactly what `state` is for: `stale` and `fresh`
      // both mean a receipt exists for this document's own scope, so
      // `exists: false` there is *the server says it is not there* — a complete
      // answer about the fields, there are none. `loading` is *never heard*,
      // and a merge folded onto that is #268's partial.
      const result = overlay(
        core,
        path,
        stored.exists ? stored.data : null,
        stored.state !== READ_STATE.loading,
        ops,
        (r) => refusals.push(r),
      );
      return stableRead(`d:${path}`, {
        data: result.data,
        exists: result.exists,
        state: stored.state,
        seenAt: stored.seenAt,
        pendingWrite: result.pendingWrite,
        partial: result.partial,
        refusals,
      });
    },
  };
}
